import CustomTooltip from '../../../../shared/CustomTooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Alert,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Switch,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import React, { useEffect, useState } from 'react';
import {
    FieldArrayWithId,
    FormProvider,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import {
    CleaningFeeType,
    ExperienceType,
    FeeApplies,
} from '../../../../enums/experience';
import {
    CommercialDetails,
    FormValues,
    PropertyDetails,
} from '../../../../types/experience';
import { generateSpaceNames } from '../../Spaces/SpaceConfiguration';
import CleaningFeeForm from './CleaningFeeForm';
import FormTextField from '../../../../shared/FormTextField';
import CalendarView from './CleaningFeeCalendarView';
import {
    capitalizeName,
    formatCurrency,
    sum,
} from '../../../../../shared/utils';
import * as z from 'zod';
import { sortSpacesByType } from '../../IndividualExperienceSpacesModal';
import { useGetIndividualOwnerAssetsQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';
import { useFormState } from '../../Form/useFormState';
import {
    CleaningFeeSchema,
    ExperiencePricingSchema,
} from '../../Form/Pricing/schema';

interface Props {
    cleaningFee: any;
    onSubmit: (data: any) => void;
    experienceType?: string;
    availableSpaces?: any;
    open: boolean;
    onClose: () => void;
    isEditing?: boolean;
}

const CleaningFeeModal = ({ cleaningFee, open, onClose, onSubmit }: Props) => {
    const [temporaryFees, setTemporaryFees] = useState<any>({
        flat: null,
        perRoom: null,
        byZone: null,
        tiered: null,
    });
    const [flexibleLatestType, setFlexibleLatestType] = useState<any>(null);
    const actualPrice = cleaningFee;
    const theme = useTheme();

    const { experience } = useFormState();

    type schemaType = z.infer<typeof CleaningFeeSchema>;

    const { control: parentControl } =
        useFormContext<z.infer<typeof ExperiencePricingSchema>>();
    const experienceSpaces = useWatch({
        control: parentControl,
        name: 'spaces',
    });

    const methods = useForm<schemaType>({
        resolver: zodResolver(CleaningFeeSchema),
        defaultValues: {
            chargeType: 'cleaningFee',
            cleaningFeeType: CleaningFeeType.Flat,
            price: undefined,
            applies: FeeApplies.PerBooking,
            spaces: [],
            guests: 1,
        },
        mode: 'onSubmit',
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, errors },
    } = methods;

    const cleaningFeeType = useWatch({
        control,
        name: 'cleaningFeeType',
    });

    const tiers = useWatch({
        control,
        name: 'tiers',
    });

    const [showFlatFee, setShowFlatFee] = useState(
        cleaningFeeType === CleaningFeeType.Flat,
    );
    const [cancel, setCancel] = useState<boolean>(false);
    const [confirmDelete, setConfirmDelete] = useState<boolean>(false);

    const handleSwitchFlatFee = () => {
        const currentValues = methods.getValues();
        const obj = { [currentValues.cleaningFeeType]: currentValues };
        setTemporaryFees({ ...temporaryFees, ...obj });
        if (currentValues.cleaningFeeType !== CleaningFeeType.Flat) {
            setFlexibleLatestType(currentValues.cleaningFeeType);
        }
        if (!showFlatFee) {
            reset({
                chargeType: 'cleaningFee',
                ...(temporaryFees['flat']
                    ? temporaryFees['flat']
                    : {
                          chargeType: 'cleaningFee',
                          cleaningFeeType: CleaningFeeType.Flat,
                          price: undefined,
                          applies: FeeApplies.PerBooking,
                      }),
            });
        }
        if (showFlatFee) {
            const details = temporaryFees[flexibleLatestType]
                ? temporaryFees[flexibleLatestType]
                : {
                      chargeType: 'cleaningFee',
                      cleaningFeeType: CleaningFeeType.PerRoom,
                      basePrice: undefined,
                      unitPrice: undefined,
                      applies: FeeApplies.PerBooking,
                      guests: 0,
                  };
            reset({
                chargeType: 'cleaningFee',
                ...details,
            });
        }
        setShowFlatFee(!showFlatFee);
    };

    useEffect(() => {
        if (!open) {
            setTemporaryFees({
                flat: null,
                perRoom: null,
                byZone: null,
                tiered: null,
            });
        }
    }, [open]);

    const handleCancel = () => {
        setConfirmDelete(false);
        if (isDirty && !cancel) {
            setCancel(true);
            return;
        }
        if (cancel) reset();
        setCancel(false);
        onClose();
    };

    const { getValues: getParentValues, setValue: setParentValue } =
        useFormContext<FormValues>();

    const getAvailableZones = () => {
        if (!experienceSpaces) return [];

        const spacesWithNames = generateSpaceNames(
            experienceSpaces as FieldArrayWithId<FormValues, 'spaces', '_id'>[],
        ).map((space: any, index: number) => ({
            ...space,
            id: space.id || index + 1,
            name: capitalizeName(space.name),
            cleaningFee: space.cleaningFee || 0,
        }));
        return sortSpacesByType(spacesWithNames);
    };

    const handleDelete = () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }
        const charges = getParentValues('charges').filter(
            (charge: any) => charge.chargeType !== 'cleaningFee',
        );
        setParentValue('charges', charges, { shouldDirty: true });

        const currentSpaces = getParentValues('spaces');
        const spaces = currentSpaces.map((space: any) => ({
            ...space,
            cleaningFee: 0,
        }));
        setParentValue('spaces', spaces, { shouldDirty: true });
        setCancel(false);
        setConfirmDelete(false);
        onClose();
    };

    useEffect(() => {
        if (!open) return;

        // When a cleaning fee exists, hydrate the form from it
        if (actualPrice) {
            const modifiedActualPrice =
                actualPrice.cleaningFeeType === CleaningFeeType.ByZone
                    ? { ...actualPrice, spaces: [...getAvailableZones()] }
                    : actualPrice;

            reset(modifiedActualPrice);
            setShowFlatFee(
                actualPrice.cleaningFeeType === CleaningFeeType.Flat,
            );
            return;
        }

        // If there is no cleaning fee (e.g., after deletion), reset to defaults
        reset({
            chargeType: 'cleaningFee',
            cleaningFeeType: CleaningFeeType.Flat,
            price: undefined,
            applies: FeeApplies.PerBooking,
            spaces: [],
            guests: 1,
        });
        setShowFlatFee(true);
    }, [open, actualPrice]);

    const currentTiers = Array.isArray(tiers) ? tiers : [];

    const maxRooms = Math.max(
        ...currentTiers.map((tier: any) => Number(tier?.rooms ?? 0) || 0),
    );

    let experienceRooms: number | undefined;

    const loggedInUser = UserRole();

    const { data: allAssets } = useGetIndividualOwnerAssetsQuery({
        url: `owners/${loggedInUser?.company}/modules`,
        sort: 'name',
        status: 'published',
    });

    const linkedModules =
        allAssets?.filter((asset: any) =>
            experience?.linkedModules.some(({ id }) => id === asset.id),
        ) || [];

    const linkedModulesBedrooms = sum(
        linkedModules.map(
            (module: FormValues) =>
                (module.details as PropertyDetails).bedrooms,
        ),
    );

    if (
        experience.type === ExperienceType.Property ||
        experience.type === ExperienceType.Boat
    ) {
        experienceRooms =
            (experience.details as PropertyDetails).bedrooms +
            (experience.linkType === 'combine' ? linkedModulesBedrooms : 0);
    }
    if (experience.type === ExperienceType.Commercial) {
        experienceRooms =
            (experience.details as CommercialDetails).privateOffices +
            (experience.details as CommercialDetails).meetingRooms;
    }

    const handleSubmitData = (data: any) => {
        const parent = getParentValues();
        const currentSpaces = Array.isArray(parent?.spaces)
            ? parent.spaces
            : [];

        if (data.cleaningFeeType === CleaningFeeType.ByZone) {
            const formSpaces = methods.getValues('spaces') ?? [];
            const hasAtLeastOneValidPrice = formSpaces.some(
                (space: any) => (space?.cleaningFee ?? 0) > 0,
            );

            if (!hasAtLeastOneValidPrice) {
                methods.setError('spaces', {
                    type: 'manual',
                    message: 'At least one zone must have a cleaning fee',
                });
                return;
            }

            const updatedSpaces = currentSpaces.map((space: any) => {
                const foundSpace = formSpaces.find((foundSpace: any) =>
                    space.demo_id
                        ? foundSpace.demo_id === space.demo_id
                        : foundSpace.id === space.id,
                );
                const cleaningFeeValue = foundSpace?.cleaningFee;
                return { ...space, cleaningFee: Number(cleaningFeeValue ?? 0) };
            });

            setParentValue('spaces', updatedSpaces, {
                shouldValidate: false,
                shouldDirty: true,
            });
        } else {
            const updatedSpaces = currentSpaces.map((space: any) => ({
                ...space,
                cleaningFee: 0,
            }));

            setParentValue('spaces', updatedSpaces, {
                shouldValidate: false,
                shouldDirty: true,
            });
        }

        if (data.cleaningFeeType === CleaningFeeType.Tiered) {
            if (experienceRooms && experienceRooms > maxRooms) {
                methods.setError('tiers', {
                    type: 'coverage',
                    message: `Tiers should cover all ${experienceRooms} rooms ${
                        linkedModulesBedrooms > 0 &&
                        experience.linkType === 'combine'
                            ? 'across the linked properties'
                            : ''
                    }`,
                });

                return;
            }
        }

        onSubmit(data);
        onClose();
    };

    useEffect(() => {
        const tiersError = methods.formState.errors.tiers as any;
        if (
            tiersError?.type === 'coverage' &&
            experienceRooms &&
            experienceRooms <= maxRooms
        ) {
            methods.clearErrors('tiers');
        }
    }, [tiers, cleaningFeeType, errors.tiers, experience, currentTiers]);

    const onNestedSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        if (event?.stopPropagation) event.stopPropagation();
        return handleSubmit(handleSubmitData)(event);
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth='md'
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '500px',
                    maxHeight: '90vh',
                },
            }}
        >
            <FormProvider {...methods}>
                <form onSubmit={onNestedSubmit}>
                    <DialogTitle
                        component='div'
                        sx={{
                            alignItems: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1, md: 14 },
                                    minWidth: 0,
                                }}
                            >
                                Cleaning Fee Configuration
                                <FormControlLabel
                                    label='Flexible Cleaning Fee'
                                    control={
                                        <Switch
                                            checked={!showFlatFee}
                                            onChange={handleSwitchFlatFee}
                                        />
                                    }
                                    sx={{ mr: 0, flexShrink: 0 }}
                                />
                            </Box>
                        </Box>
                        <IconButton
                            onClick={handleCancel}
                            size='small'
                            aria-label='close'
                            sx={{ color: 'text.primary', ml: 'auto' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers sx={{ minHeight: '400px' }}>
                        {showFlatFee && (
                            <Box>
                                <Typography variant='h6' fontWeight='bold'>
                                    Flat Fee
                                </Typography>
                                <Grid
                                    sx={{
                                        display: 'flex',
                                        gap: 0.5,
                                        alignItems: 'center',
                                        mb: 2,
                                    }}
                                >
                                    <Typography variant='body3'>
                                        This fee applies once per booking
                                    </Typography>
                                    <Grid>
                                        <CustomTooltip
                                            sx={{
                                                display: 'flex',
                                            }}
                                            title={
                                                <Grid>
                                                    <Grid
                                                        sx={{
                                                            display: 'flex',
                                                            width: '200px',
                                                            justifyContent:
                                                                'space-between',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '14px',
                                                                paddingBottom:
                                                                    '5px',
                                                            }}
                                                        >
                                                            Example:
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        sx={{
                                                            display: 'flex',
                                                            width: '200px',
                                                            justifyContent:
                                                                'space-between',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '14px',
                                                                paddingBottom:
                                                                    '5px',
                                                            }}
                                                        >
                                                            {'Base Price '}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '14px',
                                                                textAlign:
                                                                    'right',
                                                            }}
                                                        >
                                                            {formatCurrency(
                                                                2000,
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        sx={{
                                                            display: 'flex',
                                                            width: '200px',
                                                            justifyContent:
                                                                'space-between',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '14px',
                                                                paddingBottom:
                                                                    '5px',
                                                            }}
                                                        >
                                                            {'Cleaning Fee'}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '14px',
                                                                textAlign:
                                                                    'right',
                                                            }}
                                                        >
                                                            {formatCurrency(
                                                                500,
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        sx={{
                                                            mt: 1,
                                                            pt: 1,
                                                            borderTop: `1px solid ${theme.palette.faded.main}`,
                                                        }}
                                                    >
                                                        <Grid
                                                            sx={{
                                                                display: 'flex',
                                                                width: '200px',
                                                                justifyContent:
                                                                    'space-between',
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize:
                                                                        '14px',
                                                                    paddingTop:
                                                                        '2px',
                                                                }}
                                                            >
                                                                GST 10%
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontSize:
                                                                        '14px',
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                {formatCurrency(
                                                                    250,
                                                                )}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid
                                                            sx={{
                                                                display: 'flex',
                                                                width: '200px',
                                                                justifyContent:
                                                                    'space-between',
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize:
                                                                        '14px',
                                                                    paddingTop:
                                                                        '2px',
                                                                    fontWeight:
                                                                        'bold',
                                                                }}
                                                            >
                                                                {'Total'}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontSize:
                                                                        '14px',
                                                                    textAlign:
                                                                        'right',
                                                                    fontWeight:
                                                                        'bold',
                                                                }}
                                                            >
                                                                {formatCurrency(
                                                                    2750,
                                                                )}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            }
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    fontSize: '16px',
                                                }}
                                            />
                                        </CustomTooltip>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormTextField
                                            name='price'
                                            label='Cleaning Fee Amount'
                                            type='number'
                                            control={control}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position='start'>
                                                        <Typography>
                                                            $
                                                        </Typography>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        {!showFlatFee && (
                            <Grid container spacing={2}>
                                <Grid item xs={12} lg={6}>
                                    <CleaningFeeForm
                                        availableZones={getAvailableZones()}
                                        setTemporaryFees={setTemporaryFees}
                                        temporaryFees={temporaryFees}
                                        experience={experience}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <CalendarView
                                        availableZones={getAvailableZones()}
                                        experience={experience}
                                        parentControl={parentControl}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Collapse in={confirmDelete}>
                            <Alert severity='error' sx={{ mt: 2 }}>
                                Are you sure you want to delete the cleaning
                                fee?
                            </Alert>
                        </Collapse>

                        <Collapse in={cancel}>
                            <Alert severity='warning' sx={{ mt: 2 }}>
                                You have unsaved changes. Are you sure you want
                                to cancel?
                            </Alert>
                        </Collapse>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            p: 3,
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Grid>
                            {' '}
                            {actualPrice && (
                                <CustomButton
                                    onClick={handleDelete}
                                    variant='contained'
                                    color='warning'
                                >
                                    {confirmDelete
                                        ? 'Confirm Delete'
                                        : 'Delete'}
                                </CustomButton>
                            )}
                        </Grid>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <CustomButton
                                onClick={handleCancel}
                                variant='contained'
                                color='neutral'
                            >
                                Cancel
                            </CustomButton>
                            <Button
                                variant='contained'
                                type='submit'
                                color='primary'
                            >
                                Save
                            </Button>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default CleaningFeeModal;
